import React, { useState } from 'react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';

interface XSignInButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

export function XSignInButton({ onSuccess, onError, className = '' }: XSignInButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleXSignIn = async () => {
    setLoading(true);

    try {
      logger.info('X Sign-In initiated');

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'twitter',
        options: {
          redirectTo: `${window.location.origin}/auth/verify`
        }
      });

      if (error) {
        logger.error('X Sign-In error:', error);

        // Provide more specific error messages
        let errorMessage = 'Failed to sign in with X';

        if (error.message?.includes('invalid_client')) {
          errorMessage = 'X Sign-In is not properly configured. Please contact support.';
        } else if (error.message?.includes('redirect_uri_mismatch')) {
          errorMessage = 'Redirect URL mismatch. Please try again or contact support.';
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert(errorMessage);
        onError?.(errorMessage);
        return;
      }

      if (data) {
        logger.info('X Sign-In successful, redirecting...');
        onSuccess?.();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      logger.error('X Sign-In unexpected error:', err);
      alert(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleXSignIn}
      disabled={loading}
      className={`
        w-full flex items-center justify-center gap-3 px-4 py-2 
        bg-black text-white rounded-md font-medium
        hover:bg-gray-800 transition-colors duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      {/* X (Twitter) Icon */}
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>

      <span>
        {loading ? 'Signing in...' : 'Sign in with X'}
      </span>
    </button>
  );
}
